import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Alert, Box, Button } from "@mui/material";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { FinancialIcon } from "components/Icons/Icons";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";

import DebtCategorySection from "../DebtCategorySection";
import DebtGrid from "../DebtGrid";
import GrandTotalSection from "../GrandTotalSection";
import useDebtSchedules from "../hooks/useDebtSchedules";
import { groupDebtsByCategory } from "../utils/debtGrouping";

const CARD_GRID_KEY = "debt_schedule_view";

export default function DebtScheduleListWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const navigate = useNavigate();

  const [currentYear, setCurrentYear] = useState(globalState.crop_year);
  const [viewMode, setViewMode] = useState("card");

  const { debtSchedules, isLoading, error, remove } = useDebtSchedules({ year: currentYear });

  const grouped = groupDebtsByCategory(debtSchedules, currentYear);

  const allDebtsWithCategories = useMemo(
    () => [
      ...grouped.notesDueWithinOneYear.map((debt) => ({
        ...debt,
        termsCategory: "Notes Due Within One Year",
      })),
      ...grouped.notesPayable.map((debt) => ({
        ...debt,
        termsCategory: "Notes Payable",
      })),
      ...grouped.realEstateDebt.map((debt) => ({
        ...debt,
        termsCategory: "Real Estate Debt",
      })),
    ],
    [grouped]
  );

  const hasDebtsInAnyCategory = useMemo(
    () =>
      grouped.notesDueWithinOneYear.length > 0 || grouped.notesPayable.length > 0 || grouped.realEstateDebt.length > 0,
    [grouped]
  );

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: { key: "crop_year", value: currentYear },
    });
  }, [currentYear]);

  const handleEdit = (debt) => {
    navigate(`/financial/debt_schedule/${debt.id}`);
  };

  const handleDelete = async (debt) => {
    await remove(debt);
  };

  const handleAdd = () => {
    navigate(`/financial/debt_schedule/0`);
  };

  return (
    <>
      <WorkspaceHeader
        title="Debt Schedule"
        category={{
          title: "Financial Analysis",
          icon: <FinancialIcon />,
        }}
      />

      <WorkspacePageHeader
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        cardGridKey={CARD_GRID_KEY}
        isLoading={isLoading}
      />

      {error && <Alert severity="error">{error.message || "Error loading data"}</Alert>}

      {viewMode === "table" ? (
        <DebtGrid debts={allDebtsWithCategories} onDelete={handleDelete} isLoading={isLoading} />
      ) : (
        <>
          {isLoading || !hasDebtsInAnyCategory ? (
            <Box className="cardContainer">
              <EmptyCard
                text={isLoading ? "Loading Debt Schedules..." : "No Debt Schedules Found"}
                secondaryText={
                  isLoading ? `Loading data for ${currentYear}` : `Add a new debt schedule for ${currentYear}`
                }
                isLoading={isLoading}
              />
            </Box>
          ) : (
            <>
              <DebtCategorySection
                title="Notes Due Within One Year"
                debts={grouped.notesDueWithinOneYear}
                viewMode={viewMode}
                onCardClick={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
              />
              <DebtCategorySection
                title="Notes Payable"
                debts={grouped.notesPayable}
                viewMode={viewMode}
                onCardClick={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
              />
              <DebtCategorySection
                title="Real Estate Debt"
                debts={grouped.realEstateDebt}
                viewMode={viewMode}
                onCardClick={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
              />
            </>
          )}
        </>
      )}

      {!isLoading && hasDebtsInAnyCategory && (
        <GrandTotalSection title="Grand Total All Debt" debts={debtSchedules} year={currentYear} />
      )}

      <WorkspaceActionBar
        content={
          <Button variant="contained" startIcon={<AddCircleOutline />} onClick={handleAdd}>
            Add
          </Button>
        }
      />
    </>
  );
}
